/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { cn, tailwindUtils } from '@/shared/ui/utils/tailwind';
import { useTranslation } from 'react-i18next';
import { UnstyledButton } from '@/shared/ui';

export type VisitDrawerSubmitButtonProps = {
  label: string;
  isSubmitting: boolean;
  onSubmit: () => void;
};

/**
 * Footer submit button for the visit drawer (auto width, not full-bleed).
 * Shows a saving label while a submission is in flight.
 *
 * @param props - The button label, submitting flag, and submit callback.
 * @returns The visit drawer submit button.
 */
export function VisitDrawerSubmitButton({
  label,
  isSubmitting,
  onSubmit,
}: VisitDrawerSubmitButtonProps) {
  const { t } = useTranslation('features/visits');
  return (
    <UnstyledButton
      type="button"
      onClick={onSubmit}
      disabled={isSubmitting}
      aria-label={label}
      data-testid="visit-drawer-submit"
      className={cn(
        tailwindUtils.buttonBase,
        'h-10 min-w-[7.5rem] px-(--space-5)',
        'bg-(--button-primary-bg) text-(--button-primary-text)',
        'hover:bg-(--button-primary-hover-bg) hover:shadow-[var(--shadow-md)]',
        'text-[length:var(--text-body)]',
        'font-[weight:var(--font-semibold)]',
        'leading-[var(--leading-snug)]',
        'font-[family-name:var(--font-family)]',
        'disabled:cursor-not-allowed disabled:opacity-60',
      )}
    >
      {isSubmitting ? t('drawer.saving') : label}
    </UnstyledButton>
  );
}
